import {GGSCountryController} from '../../language/countryMapper/GGSCountryController';
import { SupportUtil } from './../../misc/SupportUtil';
import { NoneValueSignal } from './../../signal/NoneValueSignal';
import { FlashUIComponent } from "./FlashUIComponent";
import { BasicCustomCursor } from "./BasicCustomCursor";
import { EnvGlobalsHandler } from "../EnvGlobalsHandler";
import { IEnvironmentGlobals } from "../IEnvironmentGlobals";
import { BasicConstants } from "../constants/BasicConstants";
import { BasicModel } from "../model/BasicModel";
import { Localize } from "@goodgamestudios/localization";
import { Timer } from "@goodgamestudios/createjs-utilities";
import { TextField } from "@goodgamestudios/createjs-utilities";
import DisplayObject = createjs.DisplayObject;
import MovieClip = createjs.MovieClip;
import MouseEvent = createjs.MouseEvent;
import TimerEvent = createjs.TimerEvent;
import Rectangle = createjs.Rectangle;
import Container = createjs.Container;


export class BasicBackgroundComponent extends FlashUIComponent {
    public customCursor: BasicCustomCursor;
    private loadingTextUpdateTimer: Timer;
    private progressTimer: Timer;
    private progressStartTime: number;
    protected randomLoadingTextCount: number = 1;
    private _progressBarEnabled: boolean = true;
    public isLoaded: boolean;
    public onLoadedSignal: NoneValueSignal = new NoneValueSignal();

    constructor(disp: DisplayObject) {
        super(disp);
        disp.addEventListener("mouseover", this.bindFunction(this.onMouseOver));
        disp.addEventListener("mouseout", this.bindFunction(this.onMouseOut));
    }

    protected get backgroundView(): any {
        return (this.disp as any);
    }

    public updateLoadingProgress(percent: number): void {
        this.progressBar.progressBarStatus.scaleX = percent / 100;
    }

    // TODO: @HTML5 unhandled dynamic properties in MovieClip
    // protected get progressBar(): MovieClip {
    protected get progressBar(): any {
        return this.backgroundView.mc_progressBar;
    }

    protected get supportBtn(): MovieClip {
        return this.backgroundView.mc_bg.btn_mailsupport;
    }

    protected get background(): MovieClip {
        return this.backgroundView.mc_bg;
    }

    protected get versionTextfield(): TextField {
        return this.backgroundView.mc_bg.txt_version;
    }

    public show(): void {
        super.show();
        this.backgroundView.play();
    }

    public updatePosition(): void {
        if (this.background) {
            let bounds: Rectangle = this.background.getBounds();
            let scaleFactor: number = (this.backgroundView.stage.stageWidth / bounds.width);

            if ((bounds.height * scaleFactor) > this.backgroundView.stage.stageHeight) {
                scaleFactor = (this.backgroundView.stage.stageHeight / bounds.height);
            }
            this.backgroundView.mc_progressBar.x = this.backgroundView.stage.stageWidth * 0.5;
            this.backgroundView.mc_progressBar.y = this.backgroundView.stage.stageHeight * 0.5;
            this.background.scaleX = this.background.scaleY = scaleFactor;
            this.backgroundView.mc_bg.x = (this.backgroundView.stage.stageWidth - bounds.width * scaleFactor) * 0.5;
            this.backgroundView.mc_bg.y = (this.backgroundView.stage.stageHeight - bounds.height * scaleFactor);
        }
    }

    public get scaleFactor(): number {
        return this.background.scaleX;
    }

    public get gameNullPoint(): number {
        return this.background.x;
    }

    public startProgressBar(): void {
        // LogChannels.loading.debug("starting progressbar for fake loading");
        this.progressText = this.getRandomLoadingText();
        this.updateLoadingProgress(0);
        this.progressStartTime = Date.now();

        if (!this.progressTimer) {
            this.progressTimer = new Timer(20);
        }
        this.progressTimer.addEventListener(TimerEvent.TIMER, this.bindFunction(this.onProgressTimer));
        this.progressTimer.start();

        if (!this.loadingTextUpdateTimer) {
            this.loadingTextUpdateTimer = new Timer(BasicConstants.AUTOMATIC_LOADING_BAR_UPDATE_TIME);
        }
        this.loadingTextUpdateTimer.addEventListener(TimerEvent.TIMER, this.bindFunction(this.updateRandomLoadingText));
        this.loadingTextUpdateTimer.start();
    }

    public stopProgressBar(): void {
        // LogChannels.loading.debug("stopping progressbar fake loading");

        if (this.progressTimer) {
            this.progressTimer.stop();
            this.progressTimer.removeEventListener(TimerEvent.TIMER, this.bindFunction(this.onProgressTimer));
        }

        if (this.loadingTextUpdateTimer) {
            this.loadingTextUpdateTimer.removeEventListener(TimerEvent.TIMER, this.bindFunction(this.updateRandomLoadingText));
            this.loadingTextUpdateTimer.stop();
        }
    }

    public updateRandomLoadingText(event: TimerEvent = null): void {
        if (this.progressBar.parent == null || this.progressBar.visible == false) {
            return;
        }
        // LogChannels.loading.debug("updating random loading text");
        this.progressText = this.getRandomLoadingText();
    }

    protected getRandomLoadingText(): string {
        if (this.randomLoadingTextCount > this.maxLoadingTextCount) {
            this.randomLoadingTextCount = 1;
        }
        let identifier: string = this.loadingTextIdPrefix + this.randomLoadingTextCount;
        this.randomLoadingTextCount++;

        //TODO agoncharov: similar to FLC-1170
        if (BasicModel.languageData.languageXMLLoaded) {
            return Localize.text(identifier);
        }
        else {
            return "";
        }
    }

    public updateLoadingText(subloaderName: string): void {
        // LogChannels.loading.debug("updating static loading text for subloader " + subloaderName);

        switch (subloaderName) {
            case BasicConstants.ITEM_XML_LOADER:
                this.progressText = Localize.text("loading_text_itemXML");
                break;
            default:
                this.progressText = this.getRandomLoadingText();
                break;
        }
    }

    public set progressText(value: string) {
        if (this.env.showLoadingText && "txt_progress" in this.progressBar) {
            this.progressBar.txt_progress.text = value;
        }
    }

    protected onProgressTimer(event: TimerEvent): void {
        let progress: number = Math.sin((((Date.now() - this.progressStartTime) % BasicConstants.AUTOMATIC_LOADING_BAR_UPDATE_TIME) / BasicConstants.AUTOMATIC_LOADING_BAR_UPDATE_TIME) * (Math.PI * 0.5));
        progress = progress * 100;
        let roundedProgress: number = (progress > 0.0) ? Math.floor(progress + 0.5) : Math.floor(progress - 0.5);
        this.updateLoadingProgress(roundedProgress);
    }

    public hideProgressBar(): void {
        if (this.progressBar) {
            this.progressBar.visible = false;
            this.progressText = "";
        }
    }

    public showProgressBar(): void {
        if (this._progressBarEnabled) {
            this.progressBar.visible = true;
            this.progressText = this.getRandomLoadingText();
        }
    }

    public hide(): void {
        super.hide();
        if (this.backgroundView) {
            this.backgroundView.stop();

            for (let i: number = 0; i < this.backgroundView.numChildren; i++) {
                if ((this.backgroundView.getChildAt(i) instanceof MovieClip)) {
                    ((<MovieClip>this.backgroundView.getChildAt(i))).stop();
                }
            }
        }
    }

    public destroy(): void {
        this.disp.removeEventListener("mouseover", this.bindFunction(this.onMouseOver));
        this.disp.removeEventListener("mouseout", this.bindFunction(this.onMouseOut));
        super.destroy();
    }

    protected init(): void {

        if (this.versionTextfield) {
            this.versionTextfield.text = this.env.versionText;
            this.versionTextfield.visible = this.env.showVersion;
            this.updateLoadingProgress(0);
        }

        if (this.backgroundView && this.supportBtn) {
            this.supportBtn.visible = this.env.useexternallinks;
            this.supportBtn.addEventListener("click", this.bindFunction(this.onClick));
            this.supportBtn.addEventListener("mouseover", this.bindFunction(this.onOverSupport));
            this.supportBtn.addEventListener("mouseout", this.bindFunction(this.onOutSupport));
        }
    }

    public showVersion(): void {
        this.versionTextfield.text = this.env.versionText;
        this.versionTextfield.visible = this.env.showVersion;

        if (this.supportBtn) {
            this.supportBtn.visible = this.env.useexternallinks;
        }
    }

    protected onMouseOver(event: MouseEvent): void {
    }

    protected onMouseOut(event: MouseEvent): void {
    }

    protected onOverSupport(event: MouseEvent): void {
        let scaleFac: number = this.supportBtn.scaleX * 1.05;
        this.supportBtn.scaleX = this.supportBtn.scaleY = scaleFac;

        if (this.customCursor) {
            this.customCursor.setCursorType(BasicCustomCursor.CURSOR_CLICK);
        }
    }

    protected onOutSupport(event: MouseEvent): void {
        let scaleFac: number = this.supportBtn.scaleX * 1 / 1.05;
        this.supportBtn.scaleX = this.supportBtn.scaleY = scaleFac;

        if (this.customCursor) {
            this.customCursor.setCursorType(BasicCustomCursor.CURSOR_ARROW);
        }
    }

    protected onClick(event: MouseEvent): void {
        if (event.target == this.supportBtn) {
            SupportUtil.navigateToSupport(BasicModel.instanceProxy.selectedInstanceVO.instanceId, this.env.versionText, "-", -1, -1, GGSCountryController.instance.currentCountry.ggsLanguageCode);
        }
    }

    protected get env(): IEnvironmentGlobals {
        return EnvGlobalsHandler.globals;
    }

    public get progressBarEnabled(): boolean {
        return this._progressBarEnabled;
    }

    public set progressBarEnabled(value: boolean) {
        this._progressBarEnabled = value;

        if (this._progressBarEnabled) {
            this.showProgressBar();
        }
        else {
            this.hideProgressBar();
        }
    }

    public setProgressbarUpdateMethod( usingLoadingProcess:boolean ) :void {
        // if we want to use the real data, stop the timer that's ticking here
        if( usingLoadingProcess && this.progressTimer ) {
            this.stopProgressBar();
        } else if ( !usingLoadingProcess && !this.progressTimer) {
            this.startProgressBar();
        }
    }

    protected get loadingTextIdPrefix(): string {
        return "loading_text_";
    }

    protected get maxLoadingTextCount(): number {
        return BasicConstants.NUM_RANDOM_LOADINGTEXT_ELEMENTS_AVAILABLE;
    }
}
